import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter, takeUntil } from 'rxjs';

import { DrawerNotificationService } from '../services/drawer-notification.service';

@Component({
  selector: 'lib-cdw-drawer',
  templateUrl: './cdw-drawer.component.html',
  styleUrls: ['./cdw-drawer.component.scss'],
  standalone: false,
})
export class CdwDrawerComponent implements OnInit, OnDestroy {
  @Input() toolbarIconName = '';

  @Input() toolbarTitle = '';

  @Input() toolbarSubtitle = '';

  @Input() drawerId = '';

  @Input() drawerWidth = '300px';

  @Input() toolbarTitleBackground = '';

  @Input() toolbarTitleColor = '';

  @Input() toolbarTitleTextColor = '';

  @Input() toolbarTitleFontFamily = '';

  @Input() toolbarTitleFontSize = '';

  @Input() toolbarTitleFontWeight = '';

  @Input() toolbarSubtitleColor = '';

  @Input() toolbarSubtitleFontFamily = '';

  @Input() toolbarSubtitleFontSize = '';

  @Input() toolbarSubtitleFontWeight = '';

  @Input() hideHeaderBorder = false;

  /**
   * When true, clicking the header "X" button emits `closeRequested` instead
   * of directly notifying `DrawerNotificationService` to close. Lets the
   * parent component gate the close (e.g. show an unsaved-changes prompt).
   * Default false preserves the original fire-and-forget behavior.
   */
  @Input() interceptCloseButton = false;

  @Output() closeRequested = new EventEmitter<void>();

  public isDrawerOpen$!: Observable<boolean>;
  private isDrawerOpenSubject = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();

  constructor(private drawerNotifications: DrawerNotificationService) {}

  public ngOnInit(): void {
    if (!this.drawerId) {
      throw new Error('drawerId not defined on cdw-drawer');
    }

    // Notify that this drawer is being added
    this.drawerNotifications.notifyAddDrawer(this.drawerId, false);

    // Set up the observable
    this.isDrawerOpen$ = this.isDrawerOpenSubject.asObservable();

    // Subscribe to open drawer events for this drawer ID
    this.drawerNotifications.openDrawer$
      .pipe(
        filter(({ id }) => id === this.drawerId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isDrawerOpenSubject.next(true);
      });

    // Subscribe to close drawer events for this drawer ID
    this.drawerNotifications.closeDrawer$
      .pipe(
        filter(({ id }) => id === this.drawerId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isDrawerOpenSubject.next(false);
      });

    // Subscribe to toggle drawer events for this drawer ID
    this.drawerNotifications.toggleDrawer$
      .pipe(
        filter(({ id }) => id === this.drawerId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isDrawerOpenSubject.next(!this.isDrawerOpenSubject.value);
      });

    // Subscribe to close all drawers events
    this.drawerNotifications.closeAllDrawers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isDrawerOpenSubject.next(false);
      });
  }

  public ngOnDestroy(): void {
    this.drawerNotifications.notifyRemoveDrawer(this.drawerId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  public closeParentDrawer(): void {
    if (this.interceptCloseButton) {
      this.closeRequested.emit();
      return;
    }
    this.drawerNotifications.notifyCloseDrawer(this.drawerId);
  }
}
